import type { Project } from '../../types/project';
import type { JobCardRecord } from './jobCard.types';

export const SURVEY_NOTES_PLACEHOLDER =
  'Park on the farm track. Rear patio doors first. Customer prefers no doorbell';

export function persistedSurveyNotes(value: unknown): string {
  const notes = String(value || '');
  return notes.trim() === SURVEY_NOTES_PLACEHOLDER ? '' : notes;
}

export function asProjectMetadata(project: Pick<Project, 'metadata'>): Record<string, unknown> {
  const meta = project.metadata;
  return meta && typeof meta === 'object' && !Array.isArray(meta) ? { ...meta } : {};
}

function perCardSurveyNotes(
  project: Pick<Project, 'metadata'>,
  jobCardId: string
): string | undefined {
  const map = asProjectMetadata(project).job_card_survey_notes;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return undefined;
  const record = map as Record<string, unknown>;
  if (!(jobCardId in record)) return undefined;
  return persistedSurveyNotes(record[jobCardId]);
}

export function surveyNotesFrom(
  jobCard: Pick<JobCardRecord, 'id' | 'survey_notes'>,
  project: Pick<Project, 'metadata'>,
  isOldest: boolean
): string {
  const mapped = perCardSurveyNotes(project, jobCard.id);
  if (mapped !== undefined) return mapped;
  const fromRow = persistedSurveyNotes(jobCard.survey_notes);
  if (fromRow) return fromRow;
  if (isOldest) return persistedSurveyNotes(asProjectMetadata(project).survey_notes);
  return '';
}

/** Every job card's survey notes in one block, for a cloned workspace's first job card to inherit. */
export function clonedSurveyNotes(
  project: Pick<Project, 'metadata'>,
  jobCards: Array<Pick<JobCardRecord, 'id' | 'survey_notes'>>
): string {
  const notes = jobCards.length
    ? jobCards.map((card, index) => surveyNotesFrom(card, project, index === 0))
    : [persistedSurveyNotes(asProjectMetadata(project).survey_notes)];
  const distinct = [...new Set(notes.map((value) => value.trim()).filter(Boolean))];
  return distinct.join('\n\n');
}

export function withPerCardSurveyNotes(
  project: Pick<Project, 'metadata'>,
  jobCardId: string,
  notes: string
): Record<string, unknown> {
  const meta = asProjectMetadata(project);
  const currentMap = meta.job_card_survey_notes;
  const map = currentMap && typeof currentMap === 'object' && !Array.isArray(currentMap)
    ? { ...(currentMap as Record<string, unknown>) }
    : {};
  map[jobCardId] = persistedSurveyNotes(notes);
  return {
    ...meta,
    job_card_survey_notes: map,
  };
}
